import React, { useEffect, useRef, useState } from 'react';
import { Send, Inbox, MessageCircle, ArrowLeft, MessageSquare } from 'lucide-react';
import { Card } from './AdminPanelPrimitives';
import { StatusChip, STATUS_PALETTE, EmptyState, ADMIN_PALETTE } from './adminDesignSystem';

export const AdminSupportInboxPanel = ({
  fetchSupportThread,
  selectedSupportRequestId,
  selectedSupportThread,
  sendSupportReply,
  setSupportDraft,
  setSupportFilter,
  supportDraft,
  supportFilter,
  supportLoading,
  supportRequests,
  updateSupportStatus,
  openTicketInMessenger,
  user,
  reasonFilter = null,
  headerTitle = 'Support Requests',
  headerSubtitle = 'Reply in Messenger — the conversation continues under Support in your inbox.',
  mobileMode = false,
}) => {
  const reasonScoped = reasonFilter
    ? supportRequests.filter((r) => (r.reason || '').toLowerCase() === reasonFilter.toLowerCase())
    : supportRequests;
  const filteredRequests = supportFilter === 'all'
    ? reasonScoped
    : reasonScoped.filter((request) => supportFilter === 'solved' ? request.status === 'solved' : request.status !== 'solved');

  const shellHeight = 'clamp(620px, calc(100vh - 220px), 780px)';
  const messageBottomRef = useRef(null);
  const messageCount = selectedSupportThread?.messages?.length || 0;
  // Mobile-only: track whether chat pane is open
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  const handleSelectRequest = (requestId) => {
    fetchSupportThread(requestId);
    if (mobileMode) setMobileChatOpen(true);
  };

  useEffect(() => {
    if (!selectedSupportThread) return undefined;
    const id = window.requestAnimationFrame(() => {
      try {
        messageBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch {
        /* old browsers — noop */
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedSupportThread, selectedSupportRequestId, messageCount]);

  // ── MOBILE MODE ──────────────────────────────────────────────────
  if (mobileMode) {
    // Chat panel (when a thread is open)
    if (mobileChatOpen && selectedSupportThread) {
      return (
        <div data-testid="admin-support-inbox-layout-mobile" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Chat header with back button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 0 12px',
              borderBottom: `1px solid ${ADMIN_PALETTE.border}`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => setMobileChatOpen(false)}
              data-testid="admin-support-mobile-back"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 10,
                border: `1px solid ${ADMIN_PALETTE.border}`,
                background: '#FFFFFF',
                color: ADMIN_PALETTE.ink,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Manrope', sans-serif",
                flexShrink: 0,
              }}
            >
              <ArrowLeft size={15} /> Back
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p data-testid="admin-support-chat-user-name" style={{ margin: 0, fontWeight: 700, fontSize: 16, color: ADMIN_PALETTE.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedSupportThread.user_name}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedSupportThread.user_email}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => updateSupportStatus(selectedSupportThread.id, 'open')}
                data-testid="admin-support-open-button"
                style={{
                  padding: '7px 10px', borderRadius: 10,
                  border: `1px solid ${selectedSupportThread.status !== 'solved' ? '#16A34A' : ADMIN_PALETTE.border}`,
                  background: selectedSupportThread.status !== 'solved' ? '#DCFCE7' : '#FFFFFF',
                  color: selectedSupportThread.status !== 'solved' ? '#166534' : ADMIN_PALETTE.inkMuted,
                  cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Open
              </button>
              <button
                onClick={() => updateSupportStatus(selectedSupportThread.id, 'solved')}
                data-testid="admin-support-solved-button"
                style={{
                  padding: '7px 10px', borderRadius: 10,
                  border: `1px solid ${selectedSupportThread.status === 'solved' ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
                  background: selectedSupportThread.status === 'solved' ? ADMIN_PALETTE.ink : '#FFFFFF',
                  color: selectedSupportThread.status === 'solved' ? '#FFFFFF' : ADMIN_PALETTE.inkMuted,
                  cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Solved
              </button>
            </div>
          </div>

          {/* Message list - uses max-height so it doesn't push reply bar below the viewport on mobile */}
          <div
            data-testid="admin-support-message-list"
            style={{
              minHeight: 200,
              maxHeight: 'calc(100dvh - 340px)',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '12px 0',
            }}
          >
            {(selectedSupportThread.messages || []).map((message) => {
              const isAdminMessage = message.sender_id === user.id;
              return (
                <div
                  key={message.id}
                  data-testid={`admin-support-message-${message.id}`}
                  style={{
                    alignSelf: isAdminMessage ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background: isAdminMessage ? ADMIN_PALETTE.ink : '#F1F2F5',
                    color: isAdminMessage ? '#FFF' : ADMIN_PALETTE.ink,
                    borderRadius: isAdminMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    padding: '10px 14px',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  <p style={{ margin: 0, fontSize: 10.5, opacity: 0.65, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {isAdminMessage ? 'Admin' : selectedSupportThread.user_name}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{message.content}</p>
                </div>
              );
            })}
            <div ref={messageBottomRef} data-testid="admin-support-message-list-bottom" />
          </div>

          {/* Reply composer — writes both the legacy support_messages row
              and, if the ticket is now linked to a Messenger conversation,
              routes admin replies through Messenger via the parent. Also
              exposes a prominent "Open in Messenger" CTA that back-fills
              the user's ticket message and navigates admin to the live
              chat page. */}
          <div
            data-testid="admin-support-reply-composer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              border: `1px solid ${ADMIN_PALETTE.border}`,
              borderRadius: 16,
              background: ADMIN_PALETTE.surfaceMuted,
              flexShrink: 0,
              marginTop: 8,
              position: 'sticky',
              bottom: 80,
            }}
          >
            {typeof openTicketInMessenger === 'function' ? (
              <button
                type="button"
                onClick={() => openTicketInMessenger(selectedSupportThread.id, selectedSupportThread.user_name)}
                data-testid="admin-support-open-in-messenger"
                aria-label="Open in Messenger"
                style={{
                  background: ADMIN_PALETTE.ink,
                  border: 'none',
                  color: '#FFF',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 12,
                  fontFamily: "'Manrope', sans-serif",
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <MessageSquare size={13} strokeWidth={2.4} />
                Open in Messenger
              </button>
            ) : null}
            <input
              value={supportDraft}
              onChange={(e) => setSupportDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendSupportReply(); }}
              data-testid="admin-support-reply-input"
              placeholder="Quick reply (also opens Messenger)…"
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontSize: 14, color: ADMIN_PALETTE.ink, fontFamily: "'Manrope', sans-serif",
              }}
            />
            <button
              onClick={sendSupportReply}
              data-testid="admin-support-reply-send"
              disabled={!supportDraft.trim()}
              aria-label="Send reply"
              style={{
                background: supportDraft.trim() ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border,
                border: 'none',
                cursor: supportDraft.trim() ? 'pointer' : 'not-allowed',
                padding: '8px 10px',
                borderRadius: 10,
                color: '#FFF',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      );
    }

    // List panel (default mobile view)
    return (
      <div data-testid="admin-support-inbox-layout-mobile" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Header */}
        <div style={{ paddingBottom: 14, borderBottom: `1px solid ${ADMIN_PALETTE.border}`, marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: ADMIN_PALETTE.ink, fontFamily: "'Inria Serif', serif" }}>
            {headerTitle}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: ADMIN_PALETTE.inkSoft }}>
            {headerSubtitle}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {[
              ['open', 'Open', reasonScoped.filter((r) => r.status !== 'solved').length, 'urgent'],
              ['solved', 'Solved', reasonScoped.filter((r) => r.status === 'solved').length, 'active'],
              ['all', 'All', reasonScoped.length, 'neutral'],
            ].map(([value, label, count, tone]) => (
              <StatusChip
                key={value}
                tone={tone}
                label={label}
                count={count || 0}
                active={supportFilter === value}
                onClick={() => setSupportFilter(value)}
                dataTestId={`admin-support-filter-${value}`}
              />
            ))}
          </div>
        </div>

        {/* Request list */}
        <div data-testid="admin-support-requests-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredRequests.map((request) => (
            <button
              key={request.id}
              onClick={() => handleSelectRequest(request.id)}
              data-testid={`admin-support-request-${request.id}`}
              style={{
                width: '100%',
                border: `1px solid ${selectedSupportRequestId === request.id ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
                borderRadius: 16,
                background: selectedSupportRequestId === request.id ? ADMIN_PALETTE.surfaceTinted : '#FFFFFF',
                padding: '14px 16px',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: ADMIN_PALETTE.ink }}>{request.user_name}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: ADMIN_PALETTE.inkSoft }}>{request.user_email}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: ADMIN_PALETTE.ink }}>{request.reason}</span>
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: request.status === 'solved' ? '#166534' : '#991B1B',
                    background: request.status === 'solved' ? '#DCFCE7' : '#FEE2E2',
                    padding: '2px 8px',
                    borderRadius: 9999,
                  }}
                >
                  {request.status}
                </span>
              </div>
            </button>
          ))}
          {!filteredRequests.length && (
            <EmptyState
              icon={supportFilter === 'open' ? Inbox : MessageCircle}
              tone={supportFilter === 'open' ? 'active' : 'neutral'}
              title={supportFilter === 'open' ? 'Inbox zero ✦' : supportFilter === 'solved' ? 'Nothing solved yet' : 'No support requests'}
              body={supportFilter === 'open' ? 'No open threads right now.' : 'Solved threads will appear here.'}
              dataTestId="admin-support-empty-state"
            />
          )}
        </div>
      </div>
    );
  }
  // ── END MOBILE MODE ───────────────────────────────────────────

  return (
    <div
      data-testid="admin-support-inbox-layout"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(340px, 420px) minmax(0, 1fr)',
        gap: 14,
        height: shellHeight,
        minHeight: 0,
      }}
    >
      <Card
        data-testid="admin-support-requests-panel"
        style={{ padding: 0, overflow: 'hidden', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid #E7E7E7', flexShrink: 0 }}>
          <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700 }}>{headerTitle}</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{headerSubtitle}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {[
              ['open', 'Open', reasonScoped.filter((request) => request.status !== 'solved').length, 'urgent'],
              ['solved', 'Solved', reasonScoped.filter((request) => request.status === 'solved').length, 'active'],
              ['all', 'All', reasonScoped.length, 'neutral'],
            ].map(([value, label, count, tone]) => (
              <StatusChip
                key={value}
                tone={tone}
                label={label}
                count={count || 0}
                active={supportFilter === value}
                onClick={() => setSupportFilter(value)}
                dataTestId={`admin-support-filter-${value}`}
              />
            ))}
          </div>
        </div>
        <div data-testid="admin-support-requests-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'grid', alignContent: 'start', gap: 14 }}>
          {filteredRequests.map((request) => (
            <button key={request.id} onClick={() => fetchSupportThread(request.id)} data-testid={`admin-support-request-${request.id}`} style={{ width: '100%', border: '1px solid #E7E7E7', borderRadius: 16, background: selectedSupportRequestId === request.id ? '#F1F1F1' : '#FFFFFF', padding: '14px 16px', textAlign: 'left', cursor: 'pointer' }}>
              <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700 }}>{request.user_name}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>{request.user_email}</p>
              <p style={{ margin: '6px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>{request.customer_id || '—'}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{request.reason}</span>
                <span style={{ fontSize: 12, color: '#666' }}>{request.status}</span>
              </div>
            </button>
          ))}
          {!filteredRequests.length ? (
            <div style={{ padding: 8 }}>
              <EmptyState
                icon={supportFilter === 'open' ? Inbox : MessageCircle}
                tone={supportFilter === 'open' ? 'active' : 'neutral'}
                title={
                  supportFilter === 'open'
                    ? 'Inbox zero ✦'
                    : supportFilter === 'solved'
                      ? 'Nothing has been solved yet'
                      : 'No support requests'
                }
                body={
                  supportFilter === 'open'
                    ? "You're caught up — no open support threads right now. New requests will appear here in real time."
                    : supportFilter === 'solved'
                      ? 'Solved threads will appear here once you mark them resolved.'
                      : 'Users haven\'t opened any threads yet. They can do so from Account → Help.'
                }
                dataTestId="admin-support-empty-state"
              />
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        data-testid="admin-support-chat-panel"
        style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {selectedSupportThread ? (
          <>
            <div data-testid="admin-support-chat-header" style={{ borderBottom: '1px solid #E7E7E7', paddingBottom: 12, marginBottom: 12, flexShrink: 0 }}>
              <p data-testid="admin-support-chat-user-name" style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700 }}>{selectedSupportThread.user_name}</p>
              <p data-testid="admin-support-chat-user-email" style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>{selectedSupportThread.user_email}</p>
              <p data-testid="admin-support-chat-account-id" style={{ margin: '6px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Account ID: {selectedSupportThread.customer_id || '—'}</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => updateSupportStatus(selectedSupportThread.id, 'open')} data-testid="admin-support-open-button" style={{ padding: '8px 12px', borderRadius: 12, border: '1px solid #00C853', background: selectedSupportThread.status === 'open' ? '#E8F9EF' : '#FFFFFF', color: '#00A246', cursor: 'pointer', fontWeight: 700 }}>Open</button>
                <button onClick={() => updateSupportStatus(selectedSupportThread.id, 'solved')} data-testid="admin-support-solved-button" style={{ padding: '8px 12px', borderRadius: 12, border: '1px solid #111', background: selectedSupportThread.status === 'solved' ? '#111' : '#FFFFFF', color: selectedSupportThread.status === 'solved' ? '#FFF' : '#111', cursor: 'pointer', fontWeight: 700 }}>Solved</button>
              </div>
            </div>
            <div
              data-testid="admin-support-message-list"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}
            >
              {(selectedSupportThread.messages || []).map((message) => {
                const isAdminMessage = message.sender_id === user.id;
                return (
                  <div
                    key={message.id}
                    data-testid={`admin-support-message-${message.id}`}
                    style={{
                      alignSelf: isAdminMessage ? 'flex-end' : 'flex-start',
                      maxWidth: '78%',
                      background: isAdminMessage ? '#111' : '#F1F1F1',
                      color: isAdminMessage ? '#FFF' : '#111',
                      borderRadius: 18,
                      padding: '12px 14px',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 11, opacity: 0.7 }}>{isAdminMessage ? 'Admin' : selectedSupportThread.user_name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: 1.35 }}>{message.content}</p>
                  </div>
                );
              })}
              <div ref={messageBottomRef} data-testid="admin-support-message-list-bottom" />
            </div>
            <div data-testid="admin-support-reply-composer" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, border: '1px solid #E7E7E7', borderRadius: 16, padding: '10px 12px', flexShrink: 0 }}>
              {typeof openTicketInMessenger === 'function' ? (
                <button
                  type="button"
                  onClick={() => openTicketInMessenger(selectedSupportThread.id, selectedSupportThread.user_name)}
                  data-testid="admin-support-open-in-messenger"
                  aria-label="Open in Messenger"
                  style={{
                    background: ADMIN_PALETTE.ink,
                    border: 'none',
                    color: '#FFF',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 12,
                    fontFamily: "'Manrope', sans-serif",
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <MessageSquare size={13} strokeWidth={2.4} />
                  Open in Messenger
                </button>
              ) : null}
              <input value={supportDraft} onChange={(event) => setSupportDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') sendSupportReply(); }} data-testid="admin-support-reply-input" placeholder="Quick reply (also opens Messenger)…" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14 }} />
              <button onClick={sendSupportReply} data-testid="admin-support-reply-send" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><Send size={18} color="#111" /></button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>{supportLoading ? 'Loading support thread…' : 'Select a support request to open the live chat.'}</div>
        )}
      </Card>
    </div>
  );
};